import React, { useEffect, useState } from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { LogoutOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { tokenStorage, impersonationStorage } from '@/shared/apiClient';
import { endImpersonation } from '@/shared/impersonation';
import { AUTH_SESSION_CHANGED_EVENT, parseJwt } from '@/auth/session';

const { Text } = Typography;

function formatCountdown(secondsLeft: number): string {
  if (secondsLeft <= 0) return '00:00';
  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function ImpersonationBanner(): React.ReactElement | null {
  const navigate = useNavigate();
  const [active, setActive] = useState<boolean>(() => impersonationStorage.isActive());
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [actingAsEmail, setActingAsEmail] = useState<string>('');

  useEffect(() => {
    if (!active) return;
    // Nach Page-Reload kann der Memory-Token kurz fehlen, bevor authProvider.check()
    // die Impersonation rehydriert hat. Fallback auf den gespeicherten Impersonation-Token.
    const original = impersonationStorage.peek();
    const token = tokenStorage.getAccess() ?? original?.impersonatedAccess ?? null;
    if (!token) {
      setActive(false);
      return;
    }
    const claims = parseJwt(token);
    if (!claims || !claims.impersonator_user_id) {
      setActive(false);
      return;
    }
    // E-Mail bevorzugt aus gespeichertem Acting-User, Fallback auf localStorage.
    const emailFromStored = original?.impersonatedUser?.email;
    if (emailFromStored) {
      setActingAsEmail(emailFromStored);
    } else {
      try {
        const currentUserRaw = localStorage.getItem('sr_user');
        if (currentUserRaw) {
          const parsed = JSON.parse(currentUserRaw) as { email?: string };
          if (parsed.email) setActingAsEmail(parsed.email);
        }
      } catch {
        // ignore
      }
    }
    const updateCountdown = (): void => {
      const exp = claims.exp ?? 0;
      const left = Math.max(0, exp - Math.floor(Date.now() / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        handleEnd();
      }
    };
    updateCountdown();
    const interval = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    // React auf externe Storage-Aenderungen (Tab-uebergreifend).
    const onStorage = (): void => {
      setActive(impersonationStorage.isActive());
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, onStorage);
    };
  }, []);

  if (!active) return null;

  const claims = parseJwt(tokenStorage.getAccess() ?? '');
  const tenantId = claims?.tenant_id ?? '';

  function handleEnd(): void {
    const restored = endImpersonation();
    setActive(false);
    if (restored?.role === 'super_admin') {
      navigate('/admin/tenants');
    } else {
      navigate('/login');
    }
  }

  return (
    <Alert
      type='warning'
      showIcon
      icon={<UserSwitchOutlined />}
      style={{ borderRadius: 0, borderLeft: 0, borderRight: 0, borderTop: 0 }}
      banner
      message={
        <Space>
          <Text strong>Impersonation aktiv</Text>
          <Text>
            Du agierst als{' '}
            <Text code>{actingAsEmail || `Tenant ${tenantId.substring(0, 8)}`}</Text>
          </Text>
          <Text type='secondary'>verbleibend {formatCountdown(secondsLeft)}</Text>
        </Space>
      }
      action={
        <Button size='small' danger icon={<LogoutOutlined />} onClick={handleEnd}>
          Impersonation beenden
        </Button>
      }
    />
  );
}
